import { UploadResult } from 'Features/Projects/Table/uploadToasts'
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query'
import { requestV3 } from 'Features/Auth/axios-client'
import { ReplenishmentSiteSku } from 'Features/Projects/Scenarios/EditSettings/NetworkSettings/Replenishment/Replenishment.types'

const fetchReplenishmentSiteSkus = async (
  projectId: string,
  scenarioId: string
) => {
  const { data } = await requestV3({
    url: `/replenishment-site-skus?projectId=${projectId}&scenarioId=${scenarioId}`,
  })
  return data
}

export const useReplenishmentSiteSkus = ({
  projectId,
  scenarioId,
}: {
  projectId: string
  scenarioId: string
}): UseQueryResult<ReplenishmentSiteSku[]> => {
  return useQuery({
    queryKey: ['replenishment-site-skus', projectId, scenarioId],
    queryFn: () => fetchReplenishmentSiteSkus(projectId, scenarioId),
    select: (data) => data.data,
    gcTime: Infinity,
  })
}

// MUTATION TO UPDATE REPLENISHMENT SITE SKU DATA
export interface UpdateReplenishmentInterface {
  changedRows: Record<string, ReplenishmentSiteSku>
  projectId: string
  scenarioId: string
}

const updateReplenishmentSiteSkus = async ({
  changedRows,
  projectId,
  scenarioId,
}: UpdateReplenishmentInterface) => {
  const { data } = await requestV3({
    url: `/replenishment-site-skus?projectId=${encodeURIComponent(
      projectId
    )}&scenarioId=${encodeURIComponent(scenarioId)}`,
    method: 'PUT',
    data: changedRows,
  })
  return data
}

export const useUpdateReplenishmentSiteSkus = (
  onSuccess: () => void,
  // The API's reason, e.g. a cycle that isn't 1, 2, 4 or 8.
  onError: (message?: string) => void
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateReplenishmentSiteSkus,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          'replenishment-site-skus',
          variables.projectId,
          variables.scenarioId,
        ],
      })
      // Safety stock reads these rows: the sites' results are now out of date.
      queryClient.invalidateQueries({
        queryKey: [
          'inventory-site-summary',
          variables.projectId,
          variables.scenarioId,
        ],
      })
      onSuccess()
    },
    onError: (error: { response?: { data?: { error?: string } } }) =>
      onError(error?.response?.data?.error),
  })
}

const uploadReplenishmentSiteSkus = async ({
  file,
  projectId,
  scenarioId,
}: {
  file: File
  projectId: string
  scenarioId: string
}) => {
  const formData = new FormData()
  formData.append('file', file)
  const { data } = await requestV3({
    url: `/replenishment-site-skus?projectId=${encodeURIComponent(projectId)}&scenarioId=${encodeURIComponent(scenarioId)}`,
    method: 'POST',
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export const useUploadReplenishmentSiteSkus = (
  onSuccess: (result?: UploadResult) => void,
  onError: (error?: unknown) => void
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: uploadReplenishmentSiteSkus,
    onSuccess: (data, { projectId, scenarioId }) => {
      queryClient.invalidateQueries({
        queryKey: ['replenishment-site-skus', projectId, scenarioId],
      })
      // Safety stock reads these rows: the sites' results are now out of date.
      queryClient.invalidateQueries({
        queryKey: ['inventory-site-summary', projectId, scenarioId],
      })
      onSuccess(data)
    },
    onError,
  })
}

/**
 * Give every coupled row its line's current template cycle (POST
 * /replenishment-site-skus/update-coupled). Rows whose saved cycle predates
 * the template show cycleOutOfDate until this runs.
 */
export const useUpdateCoupledCycles = (
  projectId: string,
  scenarioId: string,
  onSuccess: (result: { rowsUpdated: number; siteKeys: number[] }) => void,
  onError: () => void
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await requestV3({
        url: `/replenishment-site-skus/update-coupled?projectId=${encodeURIComponent(
          projectId
        )}&scenarioId=${encodeURIComponent(scenarioId)}`,
        method: 'POST',
      })
      return data.data as { rowsUpdated: number; siteKeys: number[] }
    },
    onSuccess: (result) => {
      for (const key of ['replenishment-site-skus', 'inventory-site-summary']) {
        queryClient.invalidateQueries({
          queryKey: [key, projectId, scenarioId],
        })
      }
      onSuccess(result)
    },
    onError,
  })
}
